'use client';
import { JSX, ReactElement } from 'react';
import * as AlertModal from '@radix-ui/react-alert-dialog';
import { Button, FontIcon, Text } from '@intigral/shared';
import { FontIconName } from '@intigral/shared/components/FontIcon';
import styles from './alertdialog.module.sass';

/**
 * Either button text or <Button /> with all necessary props - onClick, loading, etc.
 */
type CTAButton = string | ReactElement;
interface AlertDialogProps {
    title: string | JSX.Element;
    open: boolean;
    /**
     * Callback called when `action` button is clicked
     */
    onConfirm?: (e: any) => void;
    acceptButton?: CTAButton;
    cancelButton?: CTAButton;
    children?: ReactElement;
    /**
     * Event handler called when the open state of the dialog changes.
     */
    onOpenChange?: () => void;
    /**
     * Callback called when `cancel` button is clicked
     */
    onCancel?: () => void;
    /** Name of the icon to display at the top of the dialog box */
    iconName?: null | FontIconName;
    description?: string;
    acceptButtonId?: string;
    cancelButtonId?: string;
    // Force title box to take full parent's width, i.e. ignoring parent's padding
    withFullBleedTitle?: boolean;
    acceptButtonLoading?: boolean;
}

/**
 * A modal dialog that interrupts the user with important content and expects a response.
 * https://www.radix-ui.com/primitives/docs/components/alert-dialog
 */
export const AlertDialog = ({
    title,
    description,
    open,
    onOpenChange,
    onConfirm,
    iconName = 'exclamation-mark',
    onCancel,
    acceptButton,
    cancelButton,
    children,
    acceptButtonId = '',
    cancelButtonId = '',
    withFullBleedTitle = false,
    acceptButtonLoading = false,
}: AlertDialogProps) => {
    const hasDescriptionContent = Boolean(description) || Boolean(children);
    const showCancelButton = Boolean(cancelButton);
    const showAcceptButton = Boolean(acceptButton);
    const showActions = showCancelButton || showAcceptButton;

    return (
        <AlertModal.Root open={open} onOpenChange={onOpenChange}>
            <AlertModal.Portal>
                <AlertModal.Overlay className={styles.overlay}>
                    <AlertModal.Content
                        className={styles.content}
                        // Radix requires a Description or an explicit opt-out for a11y.
                        {...(!hasDescriptionContent ? { 'aria-describedby': undefined } : {})}
                    >
                        {iconName && (
                            <span className={styles.icon}>
                                <FontIcon
                                    name={iconName}
                                    size="size60"
                                    md="size60"
                                    color={iconName === 'info' ? '' : 'red'}
                                />
                            </span>
                        )}
                        <AlertModal.Title asChild className={withFullBleedTitle ? styles.fullBleed : ''}>
                            {typeof title === 'string' ? (
                                <Text as="h3" variant="h5" margin="0" className={styles.title}>
                                    {title}
                                </Text>
                            ) : (
                                <span>{title}</span>
                            )}
                        </AlertModal.Title>
                        {description ? (
                            <AlertModal.Description asChild>
                                <Text as="p" variant="text4">
                                    {description}
                                </Text>
                            </AlertModal.Description>
                        ) : null}
                        {children ? (
                            description ? (
                                children
                            ) : (
                                <AlertModal.Description asChild>
                                    <div>{children}</div>
                                </AlertModal.Description>
                            )
                        ) : null}
                        {showActions ? (
                            <div className={styles.cols}>
                                {showCancelButton ? (
                                    <AlertModal.Cancel asChild>
                                        {typeof cancelButton === 'string' ? (
                                            <Button data-test-id={cancelButtonId} onClick={onCancel} inverse>
                                                {cancelButton}
                                            </Button>
                                        ) : (
                                            cancelButton
                                        )}
                                    </AlertModal.Cancel>
                                ) : null}
                                {showAcceptButton ? (
                                    <AlertModal.Action asChild>
                                        {typeof acceptButton === 'string' ? (
                                            <Button
                                                data-test-id={acceptButtonId}
                                                onClick={onConfirm}
                                                loading={acceptButtonLoading}
                                            >
                                                {acceptButton}
                                            </Button>
                                        ) : (
                                            acceptButton
                                        )}
                                    </AlertModal.Action>
                                ) : null}
                            </div>
                        ) : null}
                    </AlertModal.Content>
                </AlertModal.Overlay>
            </AlertModal.Portal>
        </AlertModal.Root>
    );
};
